import { ContentTemplate, Progress, TextFieldForm, nameRegExp } from 'web-library';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { CandidateProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import { fallbackDashString } from '../../../utils/StringUtils';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../modules/profile/logic/useCandidateProfileForm';
import { getContactDisplayValue } from '../../../utils/ComponentUtils';
import { ContactNumberFormItem } from '../../../components/form-items/ContactNumberFormItem';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { useStyles } from './CandidatePersonalParticulars';
import { CreateEditCandidateSchemaData } from './CreateEditCandidateSchema';
import { useCandidateNextOfKinDetails } from './useCreateEditViewCandidateAccountDetails';

interface CandidateNextOfKinDetailsProps {
  isEditState: boolean;
  formHook?: UseFormReturn<CreateEditCandidateSchemaData>;
  candidateProfileDetails?: CandidateProfileResponseObject;
}

const CandidateNextOfKinDetails = ({
  isEditState,
  formHook,
  candidateProfileDetails,
}: CandidateNextOfKinDetailsProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const {
    data: iExamsData,
    isLoading: isLoadingIEXAM,
    error: errorMasterCode,
    refetch: refetchMasterCode,
  } = useGetIExamMasterCode();

  const {
    relationshipDefaultObject,
    nextOfKinCountryCodeDefaultObject,
  } = useCandidateNextOfKinDetails({
    iExamsData,
    candidateProfileDetails,
  });

  if (
    (candidateProfileDetails?.nextOfKinRelationship && relationshipDefaultObject === undefined) ||
    (candidateProfileDetails?.nextOfKinContactNumberCountryCode &&
      nextOfKinCountryCodeDefaultObject === undefined)
  ) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ToggleDisplayEditFormItem
        id="input-nextOfKinName"
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Name')}
        valueText={fallbackDashString(candidateProfileDetails?.nextOfKinName)}
        containerClassName={classes.formElementContainer}
        EditComponent={
          <TextFieldForm
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => formHook?.setValue('nextOfKinName', e.target.value.toUpperCase())}
            label={t('admin->>UserAccountManagement.Name')}
            placeholder="Enter next of kin name"
            defaultValue={candidateProfileDetails?.nextOfKinName}
            name="nextOfKinName"
            id="input-nextOfKinName"
            type="text"
            error={!!formHook?.formState.errors.nextOfKinName}
            helperText={formHook?.formState.errors?.nextOfKinName?.message?.toString()}
            fullWidth
            isRequired
            regExpToBlock={nameRegExp}
          />
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.RelationshipToCandidate')}
        valueText={fallbackDashString(candidateProfileDetails?.nextOfKinRelationship)}
        id="input-select-nextOfKinRelationshipToCandidate"
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete
              label={t('admin->>UserAccountManagement.RelationshipToCandidate')}
              placeholder="Select relationship to candidate"
              control={formHook?.control}
              name="nextOfKinRelationshipToCandidate"
              id="input-select-nextOfKinRelationshipToCandidate"
              error={!!formHook?.formState.errors.nextOfKinRelationshipToCandidate}
              helperText={formHook?.formState.errors?.nextOfKinRelationshipToCandidate?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={iExamsData ? iExamsData.relationship : []}
              defaultValue={relationshipDefaultObject}
              getOptionLabel={(option) => typeof option === 'string' ? option : getIExamDropdownItemLabel(option)}
              renderOption={(_, option)=>getIExamDropdownItemLabel(option)}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          )
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.NextOfKinContactNo')}
        valueText={getContactDisplayValue(
          true,
          candidateProfileDetails?.nextOfKinContactNumberCountryCode,
          candidateProfileDetails?.nextOfKinContactNumber
        )}
        containerClassName={classes.formElementContainer}
        id="nextOfKinCountryCode"
        EditComponent={
          formHook && (
            <ContactNumberFormItem
              id="nextOfKinContactNo"
              formHook={formHook}
              label={t('admin->>UserAccountManagement.NextOfKinContactNo')}
              countryCode={{
                name: 'nextOfKinCountryCode',
                error: !!formHook?.formState.errors.nextOfKinCountryCode,
                helperText: formHook?.formState.errors?.nextOfKinCountryCode?.message?.toString(),
              }}
              countryCodeDefaultValue={nextOfKinCountryCodeDefaultObject}
              numberDefaultValue={candidateProfileDetails?.nextOfKinContactNumber}
              countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
              number={{
                name: 'nextOfKinContactNumber',
                error: !!formHook?.formState.errors.nextOfKinContactNumber,
                helperText: formHook?.formState.errors?.nextOfKinContactNumber?.message?.toString(),
              }}
              isSkipTransform
              placeholder="Enter next of kin contact no."
              isRequired
            />
          )
        }
      />
    </>
  );
};

export default CandidateNextOfKinDetails;
